// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, Box, Group, Progress, Stack, Text, Timeline, useMantineTheme } from '@mantine/core';
import { IconRobot, IconUser } from '@tabler/icons';
import { Dispatch, FC, useContext, useEffect, useState } from 'react';
import ProcedureStatusIcon from '../../../Atoms/ProcedureStatusIcon';
import { WorkflowS } from '../../../Service/WorkflowS';
import { ExecutionStatus, WorkflowDTO, WorkflowOperation } from '../../../Types/LogT';
import { EventContext } from '../../../Service/Context/EventContext';
import WorkflowStep from './WorkflowStep';
import WorkflowActions from './WorkflowActions';
import { useProfileShare } from '../../../Service/hooks/useProfileShare';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ProfileShare } from '../../../Service/ProfileS';
import { useJwtClaims } from '../../../Service/Context/AuthContext';

interface Props {
  workflow: WorkflowDTO;
  onDelete: () => void;
  previewOpened: boolean;
  setShowPreview: (show: boolean) => unknown;
  trigger?: number;
  updateStatus?: Dispatch<ExecutionStatus>;
  showActions?: boolean;
  disableLinks?: boolean;
}

const getProfileShareName = (profileShare?: ProfileShare) =>
  profileShare ? [profileShare.firstName, profileShare.lastName].filter(Boolean).join(' ').trim() || profileShare.email : '';

const getWorkflowStarterDescription = (
  appUserId?: number | null,
  currentUserId?: number | null,
  profileShare?: ProfileShare,
) => {
  if (appUserId === null) {
    return 'Automatisch gestartet';
  }

  if (appUserId && appUserId === currentUserId) {
    return 'Gestartet von mir';
  }

  const profileShareName = getProfileShareName(profileShare);
  return profileShareName ? `Gestartet von ${profileShareName}` : 'Gestartet von Administrator';
};

const WorkflowStarterDescription: FC<{ appUserId?: number | null; description: string }> = ({
  appUserId,
  description,
}) => {
  const theme = useMantineTheme();
  const isAutomatic = appUserId === null;
  const Icon = isAutomatic ? IconRobot : IconUser;
  const color = theme.colorScheme === 'dark' ? theme.colors.blue[4] : theme.colors.blue[7];

  return (
    <Group
      spacing={4}
      mt={2}
      noWrap
      sx={(theme) => ({
        alignSelf: 'flex-start',
        maxWidth: '100%',
        color,
      })}
    >
      <Icon size={13} stroke={2} style={{ flex: '0 0 auto' }} />
      <Text
        size="xs"
        weight={500}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {description}
      </Text>
    </Group>
  );
};

const Workflow: FC<Props> = ({
  workflow: initial,
  previewOpened,
  setShowPreview,
  onDelete,
  trigger,
  updateStatus,
  showActions = true,
  disableLinks = false,
}) => {
  const { colorScheme, colors } = useMantineTheme();
  const { dispatchProcedures } = useContext<any>(EventContext) ?? {};
  const [workflow, setWorkflow] = useState(initial);
  const { id, name, steps, currentStep, executionStatus, totalSteps, completedSteps } = workflow;
  const { teamAddonPresent } = useBookitupSubscription();
  const { userId } = useJwtClaims();
  const shouldResolveProfileShare = teamAddonPresent && workflow.appUserId !== undefined && workflow.appUserId !== userId;
  const { profileShare } = useProfileShare(shouldResolveProfileShare ? workflow.appUserId : null);
  const starterDescription = getWorkflowStarterDescription(workflow.appUserId, userId, profileShare);

  useEffect(() => {
    if (trigger) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const failed =
    executionStatus === 'FAILED' || executionStatus === 'PRECONDITION_FAILED' || executionStatus === 'ABORTED';

  useEffect(() => {
    if (updateStatus && workflow) {
      updateStatus(workflow.executionStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow]);

  const refresh = async () => {
    const resJSON = await WorkflowS.fetchWorkflow(id);
    if (resJSON) {
      const executedProcedures = WorkflowS.getExecutedProcedures(workflow, resJSON);
      if (executedProcedures.length > 0 || executionStatus !== resJSON.executionStatus) {
        if (dispatchProcedures) {
          dispatchProcedures(executedProcedures);
        }
        setWorkflow(resJSON);
      }
    }
  };

  const runOperation = async (operation: WorkflowOperation) => {
    const resJSON = await WorkflowS.runWorkflowOperation(id, operation);
    if (resJSON) {
      setWorkflow(resJSON);
    }
  };

  return (
    <Accordion.Item value={id}>
      <Box sx={{ display: 'flex' }}>
        <Accordion.Control onClick={() => setShowPreview(!previewOpened)}>
          <Stack spacing={0}>
            <Text>{name}</Text>
            <Text size="xs" color={failed ? 'red' : 'dimmed'}>
              {WorkflowS.getWorkflowDescription(workflow)}
            </Text>
            {teamAddonPresent && (
              <WorkflowStarterDescription appUserId={workflow.appUserId} description={starterDescription} />
            )}
          </Stack>
          <Progress
            animate={!executionStatus}
            mt={5}
            value={
              executionStatus === 'COMPLETED' || executionStatus === 'ABORTED' || steps.length === 0
                ? 100
                : (100 * completedSteps) / totalSteps
            }
            color={WorkflowS.getProgressBarColor(workflow, colorScheme === 'dark', colors)}
          />
        </Accordion.Control>
        {showActions && <WorkflowActions workflow={workflow} runOperation={runOperation} onDelete={onDelete} />}
      </Box>
      <Accordion.Panel>
        <Timeline active={steps.length} mt="md" bulletSize={32} lineWidth={3}>
          {steps.map((step, index) => (
            <Timeline.Item
              sx={{ marginLeft: step.alternativeStep ? 25 : 0, position: 'relative' }}
              key={step.id}
              lineVariant={step.alternativeStep ? 'dashed' : 'solid'}
              bullet={<ProcedureStatusIcon workflowStep={step} />}
              color={WorkflowS.getTimelineItemColor(step)}
            >
              <WorkflowStep
                workflowStep={step}
                currentStep={index === currentStep}
                workflow={workflow}
                reload={refresh}
                runOperation={runOperation}
                previewOpened={previewOpened}
                disableLinks={disableLinks}
              />
            </Timeline.Item>
          ))}
        </Timeline>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default Workflow;
